/**
 * Collapsible Details!-style meters under the keystone tracker. M (or the header button / the
 * Meters menu icon) collapses it to its header; the state is remembered per browser.
 */
import type { HudUpdate } from '../hud';
import { setClass } from '../dom';
import { MeterPanel } from '../meterPanel';
import type { HudWidget } from '../widgets';

const KEY = 'keystone.hudMeters.v1';
const REFRESH_MS = 250;

function load(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function save(collapsed: boolean): void {
  try {
    localStorage.setItem(KEY, collapsed ? 'collapsed' : 'open');
  } catch {
    /* private mode: not remembered */
  }
}

export class MetersWidget implements HudWidget {
  readonly panel: MeterPanel;
  private collapsed = load();
  private lastAt = -Infinity;

  constructor(host: HTMLElement) {
    this.panel = new MeterPanel(host, 'hk-meters', { collapsible: true, onCollapse: () => this.toggle() });
    this.apply();
  }

  get isCollapsed(): boolean {
    return this.collapsed;
  }

  toggle(force?: boolean): void {
    this.collapsed = force ?? !this.collapsed;
    save(this.collapsed);
    this.apply();
    this.lastAt = -Infinity; // refresh the rows at once when it opens
  }

  private apply(): void {
    setClass(this.panel.root, 'collapsed', this.collapsed);
    this.panel.collapseBtn?.setAttribute('aria-expanded', String(!this.collapsed));
  }

  update(h: HudUpdate, realNow: number): void {
    if (this.collapsed || realNow - this.lastAt < REFRESH_MS) return;
    this.lastAt = realNow;
    this.panel.update(h.meterRows(this.panel.mode, this.panel.scope), this.panel.scopeLabel(h.world));
  }
}

/** The live meters widget (the menu's Meters icon and the M key toggle it). */
export const metersRef: { current: MetersWidget | null } = { current: null };
